import { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import Select, {
	components,
	OptionProps,
	SingleValueProps,
} from "react-select";
import { common_format_utils } from "../../../../../../common/common_format_utils";
import { GuildData } from "../../../../../../common/sesh_types";
import { GuildListContext, UserContext } from "../../../sesh_router";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";

type GuildOption = {
	value: string;
	label: string;
	guild: GuildData;
};

function GuildOptionLabel({ guild }: { guild: GuildData }) {
	const iconUrl = common_format_utils.get_guild_icon_url(guild);
	return (
		<div className="flex items-center gap-2">
			{iconUrl ? (
				<img src={iconUrl} alt="" className="h-6 w-6 rounded-full" />
			) : (
				<div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-600 text-xs font-medium text-white">
					{guild.name.charAt(0).toUpperCase()}
				</div>
			)}
			<span>{guild.name}</span>
		</div>
	);
}

const CustomOption = (props: OptionProps<GuildOption>) => (
	<components.Option {...props}>
		<GuildOptionLabel guild={props.data.guild} />
	</components.Option>
);

const CustomSingleValue = (props: SingleValueProps<GuildOption>) => (
	<components.SingleValue {...props}>
		<GuildOptionLabel guild={props.data.guild} />
	</components.SingleValue>
);

export function SeshProContactPage() {
	const guild_list = useContext(GuildListContext);
	const user_context = useContext(UserContext);
	const history = useHistory();

	const [selectedGuildId, setSelectedGuildId] = useState<string>("");
	const [email, setEmail] = useState(user_context?.email ?? "");
	const [isCommercial, setIsCommercial] = useState(false);
	const [businessName, setBusinessName] = useState("");
	const [headcount, setHeadcount] = useState("");
	const [specialRequests, setSpecialRequests] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const mutation = trpc.email.seshProContact.useMutation();

	// Filter to guilds where sesh is present
	const guildsWithSesh = guild_list?.filter((g) => g.has_sesh) ?? [];

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedGuildId) {
			toast.error("Please select a server");
			return;
		}

		setIsSubmitting(true);

		const selectedGuild = guildsWithSesh.find(
			(g) => g.id === selectedGuildId
		);

		try {
			await mutation.mutateAsync({
				guildId: selectedGuildId,
				guildName: selectedGuild?.name ?? "Unknown",
				email,
				isCommercial,
				businessName: isCommercial ? businessName : undefined,
				headcount: isCommercial ? headcount : undefined,
				specialRequests: specialRequests || undefined,
			});

			setShowSuccessModal(true);
		} catch (error) {
			console.error("Failed to send contact form:", error);
			toast.error("Failed to submit request. Please try again later.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="container mx-auto max-w-2xl px-4 py-8">
			<div className="mb-6">
				<Link
					to="/dashboard/premium"
					className="inline-flex items-center gap-2 text-slate-400 hover:text-white"
				>
					<i className="fas fa-arrow-left"></i>
					Back to Premium
				</Link>
			</div>

			<div className="rounded-xl border border-slate-700 bg-slate-800/50 p-8">
				<div className="mb-6 text-center">
					<div className="mb-2 flex items-center justify-center gap-2">
						<i className="fas fa-crown text-2xl text-sesh-primary"></i>
						<h1 className="text-2xl font-bold text-white">
							Request sesh Pro
						</h1>
					</div>
					<p className="text-slate-300">
						Get premium features with API access, custom branding,
						and priority support for your server.
					</p>
				</div>

				<form onSubmit={handleSubmit} className="flex flex-col gap-5">
					{/* Server Selection */}
					<div>
						<label className="mb-2 block text-sm font-medium text-slate-200">
							Which server would you like to upgrade to sesh Pro?
						</label>
						<Select<GuildOption>
							classNamePrefix="sesh-dropdown"
							placeholder="Select a server..."
							options={guildsWithSesh.map((g) => ({
								value: g.id,
								label: g.name,
								guild: g,
							}))}
							value={
								selectedGuildId
									? {
											value: selectedGuildId,
											label:
												guildsWithSesh.find(
													(g) =>
														g.id === selectedGuildId
												)?.name ?? "",
											guild: guildsWithSesh.find(
												(g) => g.id === selectedGuildId
											)!,
									  }
									: null
							}
							onChange={(option) =>
								option && setSelectedGuildId(option.value)
							}
							components={{
								Option: CustomOption,
								SingleValue: CustomSingleValue,
							}}
							isDisabled={isSubmitting}
							isSearchable={true}
						/>
						{guildsWithSesh.length === 0 && (
							<p className="mt-2 text-sm text-amber-400">
								<i className="fas fa-exclamation-triangle mr-1"></i>
								No servers with sesh found. Please{" "}
								<a
									href="https://sesh.fyi/invite"
									target="_blank"
									rel="noreferrer"
									className="text-sesh-primary hover:underline"
								>
									invite sesh
								</a>{" "}
								to your server first.
							</p>
						)}
					</div>

					{/* Email */}
					<div>
						<label className="mb-2 block text-sm font-medium text-slate-200">
							Email Address
						</label>
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							disabled={isSubmitting}
							placeholder="your@email.com"
							className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white placeholder-slate-400 focus:border-sesh-primary focus:outline-none focus:ring-1 focus:ring-sesh-primary"
						/>
					</div>

					{/* Commercial Entity Checkbox */}
					<div className="flex items-start gap-3">
						<input
							type="checkbox"
							id="isCommercial"
							checked={isCommercial}
							onChange={(e) => setIsCommercial(e.target.checked)}
							disabled={isSubmitting}
							className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-700 text-sesh-primary focus:ring-sesh-primary"
						/>
						<label
							htmlFor="isCommercial"
							className="text-sm text-slate-200"
						>
							This is for a commercial entity (business,
							organization, or company)
						</label>
					</div>

					{/* Conditional Business Fields */}
					{isCommercial && (
						<>
							<div>
								<label className="mb-2 block text-sm font-medium text-slate-200">
									Business / Organization Name
								</label>
								<input
									type="text"
									value={businessName}
									onChange={(e) =>
										setBusinessName(e.target.value)
									}
									required={isCommercial}
									disabled={isSubmitting}
									placeholder="Acme Inc."
									className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white placeholder-slate-400 focus:border-sesh-primary focus:outline-none focus:ring-1 focus:ring-sesh-primary"
								/>
							</div>

							<div>
								<label className="mb-2 block text-sm font-medium text-slate-200">
									Approximate Headcount
								</label>
								<select
									value={headcount}
									onChange={(e) =>
										setHeadcount(e.target.value)
									}
									required={isCommercial}
									disabled={isSubmitting}
									className="w-full rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white focus:border-sesh-primary focus:outline-none focus:ring-1 focus:ring-sesh-primary"
								>
									<option value="">
										Select headcount...
									</option>
									<option value="1-10">1-10 employees</option>
									<option value="11-50">
										11-50 employees
									</option>
									<option value="51-200">
										51-200 employees
									</option>
									<option value="201-500">
										201-500 employees
									</option>
									<option value="501-1000">
										501-1000 employees
									</option>
									<option value="1000+">
										1000+ employees
									</option>
								</select>
							</div>
						</>
					)}

					{/* Special Requests */}
					<div>
						<label className="mb-2 block text-sm font-medium text-slate-200">
							Anything else you'd like us to know?
						</label>
						<textarea
							value={specialRequests}
							onChange={(e) => setSpecialRequests(e.target.value)}
							disabled={isSubmitting}
							placeholder="(Optional) Custom feature requests, questions, or anything else..."
							rows={3}
							className="w-full resize-none rounded-md border border-slate-600 bg-slate-700 px-3 py-2 text-white placeholder-slate-400 focus:border-sesh-primary focus:outline-none focus:ring-1 focus:ring-sesh-primary"
						/>
					</div>

					{/* Submit Button */}
					<div className="mt-4">
						<SeshPrimaryButton
							type="submit"
							disabled={
								isSubmitting || guildsWithSesh.length === 0
							}
							className="w-full justify-center bg-sesh-primary font-semibold !text-base-dark transition-all hover:bg-cyan-300 disabled:opacity-50"
						>
							{isSubmitting ? (
								<>
									<i className="fas fa-spinner fa-spin mr-2"></i>
									Submitting...
								</>
							) : (
								<>
									<i className="fas fa-paper-plane mr-2"></i>
									Submit Request
								</>
							)}
						</SeshPrimaryButton>
					</div>
				</form>

				<div className="mt-6 rounded-lg bg-slate-900/50 p-4 text-center text-sm text-slate-400">
					<p>
						We'll review your request and get back to you within 1-2
						business days with pricing information.
					</p>
				</div>
			</div>

			{/* Success Modal */}
			{showSuccessModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
					<div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-800 p-8 text-center shadow-2xl">
						<div className="mb-4 flex justify-center">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
								<i className="fas fa-check text-3xl text-green-400"></i>
							</div>
						</div>
						<h2 className="mb-2 text-2xl font-bold text-white">
							Request Submitted!
						</h2>
						<p className="mb-4 text-slate-300">
							Thank you for your interest in sesh Pro.
						</p>
						<div className="mb-6 rounded-lg bg-slate-900/50 p-4 text-left">
							<div className="mb-3 flex items-start gap-3">
								<i className="fas fa-envelope mt-1 text-sesh-primary"></i>
								<div>
									<p className="font-medium text-white">Confirmation Email</p>
									<p className="text-sm text-slate-400">
										A confirmation has been sent to {email}
									</p>
								</div>
							</div>
							<div className="flex items-start gap-3">
								<i className="fas fa-clock mt-1 text-sesh-primary"></i>
								<div>
									<p className="font-medium text-white">What's Next</p>
									<p className="text-sm text-slate-400">
										We'll follow up within 1-2 business days with pricing information for your server.
									</p>
								</div>
							</div>
						</div>
						<button
							onClick={() => history.push("/dashboard/premium")}
							className="w-full rounded-lg bg-sesh-primary px-6 py-3 font-semibold text-base-dark transition-colors hover:bg-cyan-300"
						>
							Back to Premium
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
